import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import SubmissionContextSection from "../../../components/admin/SubmissionContextSection";
import {
  formatLeadStatus,
  formatLeadType,
  statusBadgeClass,
} from "../../../constants/automapDealerLeadConstants";
import { fetchAdminLead, reassignAdminLead } from "../../../services/automapDealerLeadApi";
import { fetchDealerPortalDealers } from "../../../services/automapDealerInventoryApi";

function AttributionBlock({ title, touch = {} }) {
  if (!touch || Object.keys(touch).length === 0) return null;
  return (
    <div className="dealer-lead-attribution-block">
      <h4>{title}</h4>
      <p>Fuente: {touch.source || touch.firstTouchSource || "—"}</p>
      <p>Medio: {touch.medium || touch.firstTouchMedium || "—"}</p>
      <p>Campaña: {touch.utmCampaign || touch.campaign || "—"}</p>
      <p>Página: {touch.pagePath || touch.landingPage || "—"}</p>
      <p>Tipo: {touch.pageType || touch.landingPageType || "—"}</p>
    </div>
  );
}

export default function AdminLeadDetailPage() {
  const { leadId } = useParams();
  const [detail, setDetail] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [reassignId, setReassignId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDealerPortalDealers().then((data) => setDealers(data.dealers || [])).catch(() => {});
    fetchAdminLead(leadId)
      .then(setDetail)
      .catch((err) => setError(err.message || "Lead no encontrado."))
      .finally(() => setLoading(false));
  }, [leadId]);

  const reassign = async () => {
    if (!reassignId) return;
    try {
      await reassignAdminLead(leadId, reassignId);
      const refreshed = await fetchAdminLead(leadId);
      setDetail(refreshed);
    } catch (err) {
      setError(err.message || "Error al reasignar.");
    }
  };

  if (loading) return <AdminPageLayout><p className="dealer-state-box">Cargando…</p></AdminPageLayout>;
  if (!detail?.lead) {
    return (
      <AdminPageLayout>
        <p className="dealer-state-box">{error || "Lead no encontrado."}</p>
      </AdminPageLayout>
    );
  }

  const { lead, notes, events, listing } = detail;
  const firstTouch = {
    source: lead.firstTouchSource,
    medium: lead.firstTouchMedium,
    utmCampaign: lead.firstTouchCampaign,
    pagePath: lead.landingPage,
    pageType: lead.landingPageType,
  };
  const lastTouch = {
    source: lead.lastTouchSource,
    medium: lead.lastTouchMedium,
    utmCampaign: lead.lastTouchCampaign,
    pagePath: lead.sourcePage,
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Lead"
        title={lead.buyerName || "Detalle de lead"}
        description={`Dealer ${lead.dealerId}`}
      />
      <AdminAutomotiveNav active="leads" />
      <main className="admin-console__main wrap">
        <div className="import-actions">
          <Link className="dealer-portal-btn" to="/admin/leads">
            Volver
          </Link>
        </div>
        {error ? <p className="import-error">{error}</p> : null}
        <div className="dealer-lead-grid">
          <section className="dealer-state-box">
            <h3>Lead</h3>
            <p>
              <span className={`lead-badge ${statusBadgeClass(lead.status)}`}>
                {formatLeadStatus(lead.status)}
              </span>
            </p>
            <p>{formatLeadType(lead.leadType)} · Score {lead.leadScore}</p>
            <p>WhatsApp: {lead.buyerWhatsapp || "—"}</p>
            <p>Outcome: {lead.outcome} · Quality: {lead.qualityRating || "—"}</p>
          </section>
          {listing ? (
            <section className="dealer-state-box">
              <h3>Listing</h3>
              <p>{[listing.year, listing.make, listing.model].filter(Boolean).join(" ")}</p>
            </section>
          ) : null}
        </div>

        <section className="dealer-state-box">
          <h3>Atribución</h3>
          <div className="dealer-lead-grid">
            <AttributionBlock title="First touch" touch={firstTouch} />
            <AttributionBlock title="Last touch" touch={lastTouch} />
          </div>
          <p><strong>Conversión:</strong> {lead.conversionPage || "—"} ({lead.conversionPageType || "—"})</p>
          <p><strong>Acción:</strong> {lead.sourceComponent || "—"} · {lead.sourceAction || "—"}</p>
          <p><strong>UTM:</strong> {[lead.utmSource, lead.utmMedium, lead.utmCampaign, lead.utmContent, lead.utmTerm].filter(Boolean).join(" / ") || "—"}</p>
          <p><strong>Click IDs:</strong> {[lead.gclid, lead.fbclid, lead.ttclid, lead.msclkid].filter(Boolean).join(", ") || "—"}</p>
          <p><strong>Referrer:</strong> {lead.referrer || "—"} {lead.referrerDomain ? `(${lead.referrerDomain})` : ""}</p>
          <p><strong>Landing:</strong> {lead.landingPage || "—"} ({lead.landingPageType || "—"})</p>
          {lead.seoPageSlug ? <p><strong>SEO:</strong> {lead.seoPageType} · {lead.seoPageSlug}</p> : null}
          {lead.guideSlug ? <p><strong>Guía:</strong> {lead.guideSlug}</p> : null}
          <p><strong>Sesión:</strong> {lead.attributionSessionId || "—"}</p>
          <p><strong>Visitor:</strong> {lead.attributionVisitorId || "—"}</p>
          {lead.conversionPathSummary ? (
            <p><strong>Ruta:</strong> {lead.conversionPathSummary}</p>
          ) : null}
          {lead.conversionPath?.length ? (
            <ul className="lead-events-timeline">
              {lead.conversionPath.slice(-10).map((step, index) => (
                <li key={`${step.pagePath}-${index}`}>
                  {step.pagePath} · {step.pageType} · {step.sourceAction || step.eventName}
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <SubmissionContextSection
          context={{
            locale: lead.locale,
            language: lead.language,
            displayCurrency: lead.displayCurrency,
            routeLocale: lead.routeLocale,
            clientViewSlug: lead.clientViewSlug || "automap",
            pageUrl: lead.conversionPage || lead.landingPage || lead.sourcePage,
          }}
        />

        <section className="dealer-state-box">
          <select value={reassignId} onChange={(e) => setReassignId(e.target.value)}>
            <option value="">Seleccionar…</option>
            {dealers.map((dealer) => (
              <option key={dealer.id} value={dealer.id}>
                {dealer.businessName}
              </option>
            ))}
          </select>
          <button type="button" className="dealer-portal-btn primary" onClick={reassign}>
            Reasignar
          </button>
        </section>
        <section className="dealer-state-box">
          <h3>Notas</h3>
          <ul className="lead-notes-timeline">
            {(notes || []).map((note) => (
              <li key={note.id}>{note.body}</li>
            ))}
          </ul>
        </section>
        <section className="dealer-state-box">
          <h3>Eventos</h3>
          <ul className="lead-events-timeline">
            {(events || []).map((event) => (
              <li key={event.id}>
                {event.eventType} · {new Date(event.createdAt).toLocaleString("es-CO")}
              </li>
            ))}
          </ul>
        </section>
      </main>
    </AdminPageLayout>
  );
}
